import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

import type { RootState } from 'store';
import { Contract, createContract, OriginalContract } from 'types/contract';
import { OriginalMenu } from 'types/menu';

/**
 * 契約一覧の状態
 */
interface ContractListState {
  /**
   * ロード中フラグ
   */
  loading: boolean;
  /**
   * 顧客一覧 for ui
   */
  originalContracts: OriginalContract[];
  /**
   * メニュー一覧のマップ for ui
   */
  menuMap: { [key: number]: OriginalMenu };
  /**
   * 選択された顧客
   */
  selectedContract: Contract | undefined;
  /**
   * 顧客一覧の最終更新日時
   * NOTE: この値が更新されるとお知らせがリロードされる
   */
  finalUpdateTime: string | undefined;
}

/**
 * 契約一覧の初期状態
 */
const initialState: ContractListState = {
  loading: false,
  originalContracts: [],
  menuMap: {},
  selectedContract: undefined,
  finalUpdateTime: undefined,
};

/**
 * 契約一覧の slice(reducer + action)
 */
export const contractListSlice = createSlice({
  name: 'contractList',
  initialState,
  reducers: {
    init: () => initialState,
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    fetchContractsSuccess: (
      state,
      action: PayloadAction<{
        originalContracts: OriginalContract[];
        originalMenus: OriginalMenu[];
      }>,
    ) => {
      state.originalContracts = action.payload.originalContracts;
      state.menuMap = action.payload.originalMenus.reduce((prev, cur) => {
        prev[cur.id] = cur;

        return prev;
      }, <{ [key: number]: OriginalMenu }>{});
    },
    contractSelected: (
      state,
      action: PayloadAction<{ contract: Contract }>,
    ) => {
      state.selectedContract = action.payload.contract;
    },
    selectedContractCleared: (state) => {
      state.selectedContract = undefined;
    },
    finalUpdateTimeUpdated: (
      state,
      action: PayloadAction<{ finalUpdateTime: string }>,
    ) => {
      state.finalUpdateTime = action.payload.finalUpdateTime;
    },
  },
});

/**
 * 契約一覧画面の action
 */
export const contractListActions = contractListSlice.actions;

/**
 * 契約一覧画面の selector（内部用）
 */
const getloading = (state: RootState) => state.customer.contractList.loading;
const getOriginalContracts = (state: RootState) =>
  state.customer.contractList.originalContracts;
const getMenuMap = (state: RootState) => state.customer.contractList.menuMap;
// メニュー名を付与した契約一覧
const getContracts = createSelector(
  getOriginalContracts,
  getMenuMap,
  (originalContracts, menuMap) => {
    // 契約一覧を生成（メニュー名付与，日付フォーマット修正）
    const contracts = originalContracts.map((originalContract) =>
      createContract(originalContract, menuMap),
    );

    return contracts;
  },
);
const getMenus = (state: RootState) => {
  const { menuMap } = state.customer.contractList;

  return Object.values(menuMap);
};
// 未契約のメニュー一覧
const getUncontractedMenus = createSelector(
  getContracts,
  getMenus,
  (contracts, menus) => {
    const contractedMenuIds: number[] = contracts.map(
      (contract) => contract.menuId,
    );
    const uncontractedMenus = menus.filter(
      (menu) => !contractedMenuIds.includes(menu.id),
    );

    return uncontractedMenus;
  },
);
const getSelectedContract = (state: RootState) =>
  state.customer.contractList.selectedContract;

const getFinalUpdateTime = (state: RootState) =>
  state.customer.contractList.finalUpdateTime;

// 未契約のメニュー一覧（+現在設定されているメニュー）
const getUncontractedMenusAddedCurrentMenu = createSelector(
  getContracts,
  getMenus,
  getSelectedContract,
  (contracts, menus, selectedContract) => {
    const contractedMenuIds: number[] = contracts.map(
      (contract) => contract.menuId,
    );
    const uncontractedMenusAddedCurrentMenu = menus.filter(
      (menu) =>
        !(
          contractedMenuIds.includes(menu.id) &&
          selectedContract?.menuId !== menu.id
        ),
    );

    return uncontractedMenusAddedCurrentMenu;
  },
);

/**
 * 顧客一覧画面の selector
 */
export const contractListSelectors = {
  loading: getloading,
  contracts: getContracts,
  menuMap: getMenuMap,
  uncontractedMenus: getUncontractedMenus,
  uncontractedMenusAddedCurrentMenu: getUncontractedMenusAddedCurrentMenu,
  selectedContract: getSelectedContract,
  finalUpdateTime: getFinalUpdateTime,
} as const;
